export function deleteUrlParams(names: string[], urlSearchParams = new URLSearchParams(location.search)) {
  names.forEach((name) => urlSearchParams.delete(name));
  history.replaceState(null, '', getUrl(urlSearchParams));
}

export function deleteUrlParam(name: string, urlSearchParams = new URLSearchParams(location.search)) {
  urlSearchParams.delete(name);
  history.replaceState(null, '', getUrl(urlSearchParams));
}

export function getUrlParam<T extends string = string>(
  name: string,
  urlSearchParams = new URLSearchParams(location.search),
) {
  return urlSearchParams.get(name) as T | null;
}

function getUrl(urlSearchParams: URLSearchParams) {
  const baseUrl = location.href.split('?')[0];
  if (urlSearchParams.size === 0) {
    return baseUrl;
  }
  return `${baseUrl}?${urlSearchParams}`;
}

export function hasUrlParam(name: string, urlSearchParams = new URLSearchParams(location.search)) {
  return urlSearchParams.has(name);
}

/**
 * Parses the query part of the given url string and returns a key-value object with its parameters.
 * The value is an array because a key can exist multiple times.
 *
 * @return Parameters as a { key: value[] } object
 */
export function getUrlParams(urlString: string) {
  const [, queryString] = urlString.split('?');
  const params: Record<string, string[]> = {};
  const searchParams = new URLSearchParams(queryString);
  searchParams.forEach((value, key) => {
    const val = params[key];
    if (val === undefined) {
      params[key] = [value];
    } else {
      val.push(value);
    }
  });
  return params;
}

/**
 * Extend a given URL with the given parameters map as query parameters.
 *
 * Note: If the given URL already contains one or more parameters with the same name as one of the new
 * parameters specified, it or they will be replaced by the new value.
 *
 * @param urlString - The URL to extend
 * @param params - A map-like object of parameters top add to the URL
 * @return A string containing the original URL with the added query parameters
 */
export function extendUrlParams(urlString: string, params: Record<string, unknown | unknown[]> = {}) {
  const [baseUrl, queryString] = urlString.split('?');
  const searchParams = new URLSearchParams(queryString);
  for (const [paramName, paramValue] of Object.entries(params)) {
    if (Array.isArray(paramValue)) {
      paramValue.forEach((value) => searchParams.append(paramName, value));
    } else {
      searchParams.set(paramName, String(paramValue));
    }
  }
  const paramsUrlPart = searchParams.toString();
  return paramsUrlPart ? baseUrl + '?' + paramsUrlPart : baseUrl;
}
